/******************************************************************************
 * PhotoCategoryForm: create/edit form for a photo category.
 * Fields: translatable label + enabled + the list of work-order (intervention)
 * types it applies to + the required min / optional max photo count.
 *****************************************************************************/

/* Imports */
import { t } from "i18next";
import { useEffect, useState } from "react";
/***/

/* Components */
import TranslationEdit from "../translation-edit/TranslationEdit";
import Switch from "../../switch/Switch";
import { api } from "../../../services/api/api";
import { reactSelectTheme } from "./reactSelectTheme";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import ApplicationFieldSelect from "./ApplicationFieldSelect";
/***/

/**
* loadTypeOptions: async loader for the work-order type multi-select
* @param input - search term
*/
const loadTypeOptions = (input?: string): Promise<{ value: string, label: string }[]> => {
  const queries = input
    ? { queries: [{ type: "SEARCH", operator: "LIKE", field: ["label", "id"], value: input }] }
    : { queries: [] };
  return api.post("users/workorder_types/search", queries)
    .then((ret: any) => ((ret?.data || ret || []) as any[]).map((type) => ({ value: type.id, label: type.label })))
    .catch(() => []);
};

/**
* PhotoCategoryForm: create/edit photo category form
* @param data - data to edit
* @param onChange - on field change event
* @param submitted - is form submitted (drives error styling)
*/
export default function PhotoCategoryForm({
  data,
  onChange,
  submitted
}: {
  data?: EasyTech.PhotoCategory,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  const [languages, setLanguagesState] = useState<EasyTech.Language[]>([]);

  /**
  * useEffect: load languages for the translation editor
  */
  useEffect(() => {
    api.get("users/languages")
      .then((ret) => setLanguagesState(ret as EasyTech.Language[]))
      .catch((err) => console.error(err));
  }, []);
  /***/

  const selectedTypeOptions = (data?.workOrderTypeIds || []).map((id, i) => ({
    value: id,
    label: data?.workOrderTypeLabels?.[i] || id
  }));

  const maxInvalid = submitted && data?.maxPhotos !== null && data?.maxPhotos !== undefined
    && (data.maxPhotos < 0 || data.maxPhotos < (data.minPhotos ?? 0));

  return (
    <>
      <div className="field">
        <label htmlFor="label">{t("label")}</label>
        <input type="text"
          value={data?.label || ""}
          onChange={(e) => onChange("label", e.target.value)}
          className={`${(submitted && (!data || !data?.label)) && "error"}`}
          placeholder={t("Label")} />
      </div>

      <div className="field">
        <label htmlFor="translation">{t("Translation")}</label>
        <TranslationEdit key={data?.id} data={data?.translations} onChange={(v) => onChange("translations", v)} languages={languages} />
      </div>

      <div className="field">
        <label htmlFor="workorder-type">{t("common:selectWorkorderType")}</label>
        <MultiCheckSelect
          cacheOptions
          defaultOptions
          loadOptions={loadTypeOptions}
          value={selectedTypeOptions}
          onChange={(opts: { value: string, label: string }[] | null) => {
            const arr = opts || [];
            onChange("workOrderTypeIds", arr.map((o) => o.value));
            onChange("workOrderTypeLabels", arr.map((o) => o.label));
          }}
          theme={reactSelectTheme}
          className="w-full"
          placeholder={t("common:selectWorkorderType")} />
      </div>

      <div className="field">
        <label htmlFor="minPhotos">{t("Min photos")}</label>
        <input type="number"
          min={0}
          value={data?.minPhotos ?? ""}
          onChange={(e) => onChange("minPhotos", e.target.value === "" ? null : Number(e.target.value))}
          className={`${(submitted && data?.minPhotos !== null && data?.minPhotos !== undefined && data.minPhotos < 0) && "error"}`}
          placeholder={t("Min photos")} />
      </div>

      <div className="field">
        <label htmlFor="maxPhotos">{t("Max photos")}</label>
        <input type="number"
          min={0}
          value={data?.maxPhotos ?? ""}
          onChange={(e) => onChange("maxPhotos", e.target.value === "" ? null : Number(e.target.value))}
          className={`${maxInvalid && "error"}`}
          placeholder={t("Max photos")} />
      </div>

      <div className="field">
        <label htmlFor="applicationField">{t("settings:photoCategories.applicationField.label")}</label>
        <ApplicationFieldSelect
          value={data?.applicationField}
          onChange={(v) => onChange("applicationField", v)} />
      </div>

      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Enable")}</label>
        <Switch checked={data?.enabled}
          onChange={(e) => onChange("enabled", e)} />
      </div>
    </>
  );
}
/***/
